import { AppMapas } from './Constantes'
import dayjs from './DayjsUtil'

/**
 * @description Calcula la cantidad de campos para tareas en una condicionante a partir de una periodicidad
 *              fecha termino y actual.
 *
 * @author ana.carrasco  Hábil MX
 * @version 1.0.0, 31/05/2023
 * @param fechaTermino Es la fecha de vencimiento o fin para referencia en el calculo de elementos
 * @param fechaActual Fecha del dia actual
 * @param periodicidadEnMeses Cantidad en meses en las que se generará un campo
 *                            (mensual:1, trimestal:3, semestral:6, anual:12)
 * @param cantidadDuracion Periodos de una condicionante en una autorizacion indefinida
 * @returns elementos generados para los campos de tareas
 */
const elementosCalculadosPorPeriodicidad = (
  fechaTermino: string | undefined,
  fechaActual: Date,
  periodicidadEnMeses: number,
  cantidadDuracion: number | undefined
) => {
  let diferenciaEnMeses = 0
  const diaMilisegundos = 86400000 * 30
  let elementosCalculados: any
  if (fechaTermino) {
    const fechaIngresadaEnFuncion = new Date(fechaTermino)
    const diferenciaMs = fechaIngresadaEnFuncion.getTime() - fechaActual.getTime()
    //FECHA VENCIMIENTO
    if (diferenciaMs >= 0) {
      diferenciaEnMeses = Math.floor(diferenciaMs / diaMilisegundos)
      if (periodicidadEnMeses > 0 && diferenciaEnMeses > periodicidadEnMeses) {
        elementosCalculados = Math.floor(diferenciaEnMeses / periodicidadEnMeses)
      } else {
        elementosCalculados = 1
      }
    }
  } else {
    elementosCalculados = cantidadDuracion
  }
  // Limite funcional de seguridad para no generar listas de tareas demasiado grandes en UI.
  if (elementosCalculados >= 40) elementosCalculados = 40
  return elementosCalculados
}
/**
 * @description Calcula la cantidad de elementos para tareas y las
 *              fechas de inicio y termino para cada campo.
 *
 * @author ana.carrasco  Hábil MX
 * @version 1.0.0, 06/01/2023
 * @param fechaTermino Es la fecha de vencimiento o fin para referencia en el calculo de elementos
 * @param cantidadDuracion Cantidad en numero de la peridicidad.
 * @param periodicidad Unidad de periodicidad. (MESES, DIAS, ETC)
 * @returns
 */
export const calcularCantidadElementosPeriodicidad = (
  fechaTermino: string | undefined,
  cantidadDuracion: number | undefined,
  periodicidad: string
) => {
  const fechaActual = new Date(Date())
  let fechaInicio = dayjs(fechaActual)
  const arregloFechaInicio: any = []
  const arregloFechaTermino: any = []
  const periodicidadEnMeses =
    AppMapas.MAPA_PERIODICIDAD_CONDICIONANTE_A_MESES.get(periodicidad) ?? 0

  const elementosCalculados = elementosCalculadosPorPeriodicidad(
    fechaTermino,
    fechaActual,
    periodicidadEnMeses,
    cantidadDuracion
  )

  // Genera fechas sugeridas para tareas periodicas respetando el vencimiento de la autorizacion.
  for (let i = 0; i <= elementosCalculados; i++) {
    if (i === 0) {
      fechaInicio = dayjs(fechaActual)
      arregloFechaInicio.push(fechaInicio.toDate().toDateString())
    } else {
      fechaInicio = fechaInicio.add(periodicidadEnMeses, 'month')
      arregloFechaInicio.push(fechaInicio.toDate().toDateString())
    }
    if (periodicidadEnMeses > 0) {
      const nuevaFechaTerminoGenerada = fechaInicio.add(periodicidadEnMeses, 'month')
      if (dayjs(fechaTermino).diff(dayjs(nuevaFechaTerminoGenerada)) <= 0) {
        arregloFechaTermino.push(dayjs(fechaTermino))
      } else {
        arregloFechaTermino.push(nuevaFechaTerminoGenerada.toDate().toDateString())
      }
    } else {
      const fechaTermino = dayjs().add(1, 'day')
      arregloFechaTermino.push(fechaTermino)
    }
  }

  return [Number(elementosCalculados), arregloFechaInicio, arregloFechaTermino]
}

/**
 * @description Recalcula los arreglos para las fechas de inicio y de termino al cambiar los campos de la primera tarea
 *              de una condicionante.
 *
 * @author ana.carrasco  Hábil MX
 * @version 1.0.0, 21/12/2023
 * @param fechaInicioTarea Fecha de inicio de la primera tarea generada por periodicidad en la condicionante.
 * @param fechaTerminoTarea Fecha de termino de la primera tarea generada por periodicidad en la condicionante.
 * @param cantElementos número de elementos "no manuales" existentes en el arreglo de tareas en una condicionante.
 * @param periodicidad periodicidad de la condicionante (MENSUAL, TRIMESTRAL, SEMESTRAL, ANUAL, ÚNICA)
 * @param fechaVencimientoAutorizacion  Fecha de vencimiento de la autorización para no generar fechas fuera de ese rango.
 * @returns nuevo arreglo de fechas de inicio y fechas de término de las tareas generadas no manualmente.
 */
export const generarNuevoArregloFechas = (
  fechaInicioTarea: string | undefined,
  fechaTerminoTarea: string | undefined,
  cantElementos: number,
  periodicidad: string,
  fechaVencimientoAutorizacion?: string
) => {
  const arregloFechaInicio: any = []
  const arregloFechaTermino: any = []

  const fechaInicioConstante = dayjs(fechaInicioTarea)
  let fechaInicio = dayjs(fechaInicioTarea)
  let fechaTermino = dayjs(fechaTerminoTarea)
  const diferenciaDias = dayjs(fechaTermino).diff(dayjs(fechaInicio), 'day')

  const periodicidadEnMeses =
    AppMapas.MAPA_PERIODICIDAD_CONDICIONANTE_A_MESES.get(periodicidad) ?? 0

  if (!fechaInicioTarea || !fechaTerminoTarea) {
    arregloFechaInicio.push(null)
    arregloFechaTermino.push(null)
  } else {
    // Mantiene la misma duracion entre inicio y termino al desplazar tareas generadas.
    for (let i = 0; i <= cantElementos; i++) {
      fechaInicio = fechaInicioConstante.add(periodicidadEnMeses * i, 'month')
      arregloFechaInicio.push(fechaInicio.toDate().toString())
      fechaTermino = fechaInicio.add(diferenciaDias, 'day')

      if (
        fechaVencimientoAutorizacion &&
        dayjs(fechaVencimientoAutorizacion).diff(dayjs(fechaTermino)) >= 0
      ) {
        arregloFechaTermino.push(fechaTermino.toDate().toDateString())
      } else if (
        fechaVencimientoAutorizacion &&
        dayjs(fechaVencimientoAutorizacion).diff(dayjs(fechaTermino)) < 0
      ) {
        arregloFechaTermino.push(fechaVencimientoAutorizacion)
      } else {
        arregloFechaTermino.push(fechaTermino.toDate().toDateString())
      }
    }
  }

  return [arregloFechaInicio, arregloFechaTermino]
}
